<template>
  <div class="uc-information-bar__side">
    <div
      v-for="(item) in validItems"
      :key="item.key"
      class="uc-information-bar__item"
      :class="{
        'cursor-pointer': item.clickable,
        'cursor-help': !item.clickable && item.hint,
      }"
      :title="t(`${item.hint}`)"
      @click="emitItemClicked(item)"
    >
      <slot :item="item">
        <UCSvgIcon
          v-if="item.icon"
          :name="item.icon"
          width="16px"
          height="16px"
        />

        <div
          v-if="item.text"
          class="uc-information-bar__item__text"
        >
          {{ item.text }}
        </div>
      </slot>
    </div>
  </div>
</template>

<script setup>
// @ts-check
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import UCSvgIcon from './UCSvgIcon.vue';

/** @import { PropType } from 'vue' */
/** @import { InformationBarItem } from '@/composables/useInformationBar' */

const props = defineProps({
  items: {
    type: /** @type {PropType<InformationBarItem[]>} */ (
      Array
    ),
    required: true,
  },
});

const emit = defineEmits(['itemClicked']);

const { t } = useI18n();

const validItems = computed(() => (props.items ? props.items.filter(Boolean) : []));

/**
 * Emits a click event for the item if it's clickable.
 *
 * @param {InformationBarItem} item - The item that was clicked.
 * @returns {void}
 */
function emitItemClicked(item) {
  if (item.clickable) {
    emit('itemClicked', item);
  }
}
</script>

<style scoped lang="scss">
.uc-information-bar__side {
  display: flex;
  flex-direction: row;
  gap: var(--information-bar-item-gap);
  overflow: var(--information-bar-overflow);
  text-overflow: var(--information-bar-text-overflow);
  white-space: var(--information-bar-white-space);
}

.uc-information-bar__item {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: var(--information-bar-icon-gap);

  // All items in the same line
  overflow: inherit;
  text-overflow: inherit;
  white-space: inherit;

  > div {
    overflow: inherit;
    text-overflow: inherit;
    white-space: inherit;
  }

  &__text {
    line-height: initial;
  }
}

.cursor-help {
  cursor: help;
}

.cursor-pointer {
  cursor: pointer;
}
</style>
